import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Check, CheckCircle2, Loader2, ScanFace, ShieldAlert, Upload, X } from 'lucide-react';
import Header from '../../compos/Header/Header';
import Footer from '../../compos/Footer/Footer';
import { useUser } from '../../UserContext';
import { BACKEND_URL } from '../../config';

// Keep in sync with the backend's MAX_FACTOMETER_BYTES.
const MAX_BYTES = 200 * 1024 * 1024;

type Verdict = 'Real' | 'Fake' | 'NoFace';
// thumb: small JPEG data URL of the face crop the model checked (never stored server-side).
type Face = { timeSec: number | null; fake: boolean; thumb: string };
// fakeScore: share of checked faces that look fake (0–1), null when no face was found.
type Result = {
	verdict: Verdict;
	fakeScore: number | null;
	framesAnalyzed: number;
	facesAnalyzed: number;
	faces: Face[];
};
type Step = 'PICK' | 'READY' | 'ANALYZING' | 'RESULT';

const VERDICT_UI: Record<Verdict, { title: string; note: string; Icon: typeof ScanFace; cls: string }> = {
	Real: {
		title: 'Looks real',
		note: 'No signs of manipulation were found in the faces we checked.',
		Icon: CheckCircle2,
		cls: 'bg-green-50 border-green-200 text-green-700',
	},
	Fake: {
		title: 'Likely fake',
		note: 'The faces in this file show signs of manipulation.',
		Icon: ShieldAlert,
		cls: 'bg-red-50 border-red-200 text-red-700',
	},
	NoFace: {
		title: 'No face found',
		note: 'The Fact-o-meter only judges faces. Try a clip where a face is clearly visible.',
		Icon: ScanFace,
		cls: 'bg-gray-50 border-gray-200 text-gray-600',
	},
};

const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// The model is never truly 0% or 100% sure, so don't print those.
const formatScore = (s: number) => (s <= 0.005 ? '<1%' : s >= 0.995 ? '>99%' : `${Math.round(s * 100)}%`);
// Meter zones; the headline verdict itself flips at 50% (backend FAKE_THRESHOLD).
const scoreColor = (s: number) => (s < 0.3 ? 'text-green-600' : s <= 0.7 ? 'text-amber-600' : 'text-red-600');

function FactOMeter() {
	const { user, isAuthLoading } = useUser();
	const location = useLocation();
	const navigate = useNavigate();

	const [step, setStep] = useState<Step>('PICK');
	const [file, setFile] = useState<File | null>(null);
	const [result, setResult] = useState<Result | null>(null);
	const [error, setError] = useState('');

	const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
	useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

	if (isAuthLoading) {
		return (
			<div className='flex justify-center items-center h-screen'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#022c56]'></div>
			</div>
		);
	}

	if (user == null) {
		return (
			<div>
				<Navigate to='/login' state={{ from: location.pathname }} />
			</div>
		);
	}

	const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0];
		e.target.value = '';
		if (!picked) return;
		if (!/^(video|image)\//.test(picked.type)) return setError('Please choose a video or an image.');
		if (picked.size > MAX_BYTES) return setError('That file is over 200 MB. Please trim it or pick a shorter clip.');
		setError('');
		setFile(picked);
		setStep('READY');
	};

	const reset = () => {
		setFile(null);
		setResult(null);
		setError('');
		setStep('PICK');
	};

	const analyze = async () => {
		if (!file) return;
		setStep('ANALYZING');
		setError('');
		const body = new FormData();
		body.append('file', file);
		try {
			const res = await fetch(`${BACKEND_URL}/api/fact-o-meter`, { method: 'POST', body, credentials: 'include' });
			if (res.status === 401) return navigate('/login', { state: { from: location.pathname } });
			const data = await res.json().catch(() => null);
			if (!res.ok) throw new Error(data?.message || `Check failed (${res.status}). Please try again.`);
			setResult(data);
			setStep('RESULT');
		} catch (err) {
			const msg = err instanceof Error ? err.message : '';
			setError(msg && msg !== 'Failed to fetch' ? msg : 'Could not reach the server. Check your connection and try again.');
			setStep('READY');
		}
	};

	const verdictUi = result && VERDICT_UI[result.verdict];
	const faces = result?.faces ?? [];
	const fakeFaces = faces.filter((f) => f.fake).length;

	return (
		<div className='min-h-screen flex flex-col bg-gray-50'>
			<Header />
			<main className='flex-1 pt-[var(--header-height,80px)] px-4 pb-16'>
				<div className='max-w-2xl mx-auto mt-8'>
					<div className='text-center mb-8'>
						<div className='inline-flex w-14 h-14 rounded-full bg-[#022c56]/10 items-center justify-center mb-3'>
							<ScanFace className='w-7 h-7 text-[#022c56]' />
						</div>
						<h1 className='text-3xl font-bold text-[#022c56]'>Fact-o-meter</h1>
						<p className='text-gray-600 mt-2'>
							Upload a video or photo and our AI checks the faces in it for signs of deepfake manipulation.
						</p>
					</div>

					<div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
						{step === 'PICK' && (
							<div className='rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 hover:bg-gray-100/80 transition-all duration-300 relative group flex flex-col items-center justify-center aspect-[16/9] cursor-pointer'>
								<input
									className='absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20'
									type='file'
									accept='video/*,image/*'
									onChange={pickFile}
								/>
								<div className='w-14 h-14 bg-white shadow-sm rounded-full flex items-center justify-center text-gray-400 mb-4 group-hover:scale-110 group-hover:text-[#022c56] transition-all duration-300'>
									<Upload className='w-7 h-7' />
								</div>
								<span className='font-bold text-gray-400 group-hover:text-[#022c56] transition-colors text-sm tracking-widest uppercase'>
									Click or drop a video / photo
								</span>
								<span className='text-xs text-gray-400 mt-2'>MP4, MOV, WEBM, JPG, PNG · up to 200 MB</span>
							</div>
						)}

						{step !== 'PICK' && file && (
							<div className='bg-black rounded-xl overflow-hidden flex items-center justify-center'>
								{file.type.startsWith('video/') ? (
									<video src={previewUrl} controls className='max-h-[420px] w-full' />
								) : (
									<img src={previewUrl} alt='Selected file' className='max-h-[420px] object-contain' />
								)}
							</div>
						)}

						{error && (
							<div className='mt-4 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm text-left'>
								<AlertCircle className='w-4 h-4 mt-0.5 flex-shrink-0' />
								{error}
							</div>
						)}

						{step === 'READY' && (
							<div className='mt-5 flex flex-col sm:flex-row gap-3'>
								<button
									onClick={analyze}
									className='flex-1 bg-[#022c56] hover:bg-[#034068] text-white font-semibold py-3 rounded-xl transition-colors'
								>
									Check if it's real
								</button>
								<button
									onClick={reset}
									className='flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold py-3 rounded-xl transition-colors'
								>
									Choose another file
								</button>
							</div>
						)}

						{step === 'ANALYZING' && (
							<div className='mt-6 flex flex-col items-center text-gray-600'>
								<Loader2 className='w-8 h-8 animate-spin text-[#022c56]' />
								<p className='mt-3 font-medium'>Analyzing faces…</p>
								<p className='text-xs text-gray-400'>Long videos can take up to a minute.</p>
							</div>
						)}

						{step === 'RESULT' && result && verdictUi && (
							<div className='mt-5'>
								<div className={`rounded-2xl border p-5 flex items-start gap-4 text-left ${verdictUi.cls}`}>
									<verdictUi.Icon className='w-10 h-10 flex-shrink-0' />
									<div>
										<p className='text-2xl font-bold m-0'>{verdictUi.title}</p>
										<p className='m-0 mt-1'>{verdictUi.note}</p>
										<p className='m-0 mt-2 text-sm opacity-80'>
											{faces.length > 0
												? `${fakeFaces} of ${faces.length} face${faces.length === 1 ? '' : 's'} looked manipulated · `
												: ''}
											{result.framesAnalyzed} frame{result.framesAnalyzed === 1 ? '' : 's'} checked.
										</p>
									</div>
								</div>
								{result.fakeScore != null && (
									<div className='mt-4 text-left'>
										<div className='flex justify-between items-baseline'>
											<p className='text-sm font-semibold text-gray-700 m-0'>Fake score</p>
											<p className={`text-2xl font-bold m-0 ${scoreColor(result.fakeScore)}`}>
												{formatScore(result.fakeScore)}
											</p>
										</div>
										<div
											role='meter'
											aria-label='Fake score'
											aria-valuemin={0}
											aria-valuemax={100}
											aria-valuenow={Math.round(result.fakeScore * 100)}
											className='relative mt-2 h-3 rounded-full bg-gradient-to-r from-green-400 via-amber-300 to-red-500'
										>
											<div
												className='absolute top-1/2 w-4 h-4 rounded-full bg-white border-2 border-gray-800 shadow -translate-x-1/2 -translate-y-1/2'
												style={{ left: `${result.fakeScore * 100}%` }}
											/>
										</div>
										<div className='flex justify-between text-[11px] text-gray-400 mt-1'>
											<span>Real</span>
											<span>Unsure</span>
											<span>Fake</span>
										</div>
									</div>
								)}
								{faces.length > 0 && (
									<div className='mt-4 text-left'>
										<p className='text-sm font-semibold text-gray-700 m-0 mb-2'>Faces we checked</p>
										<div className='flex flex-wrap gap-3'>
											{faces.map((f, i) => (
												<div key={i} className='flex flex-col items-center'>
													<div className='relative'>
														<img
															src={f.thumb}
															alt={`Face ${i + 1}: ${f.fake ? 'looked manipulated' : 'looked real'}`}
															className={`w-16 h-16 rounded-lg object-cover border-2 ${f.fake ? 'border-red-400' : 'border-green-300'}`}
														/>
														<span
															className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white ${f.fake ? 'bg-red-500' : 'bg-green-500'}`}
														>
															{f.fake ? <X className='w-3 h-3' /> : <Check className='w-3 h-3' />}
														</span>
													</div>
													{f.timeSec != null && (
														<span className='text-[11px] text-gray-400 mt-1'>{formatTime(f.timeSec)}</span>
													)}
												</div>
											))}
										</div>
									</div>
								)}
								<p className='text-xs text-gray-400 mt-3'>
									This is an AI estimate and can be wrong. Don't rely on it alone before sharing or reporting.
								</p>
								<button
									onClick={reset}
									className='mt-4 w-full bg-[#022c56] hover:bg-[#034068] text-white font-semibold py-3 rounded-xl transition-colors'
								>
									Check another file
								</button>
							</div>
						)}
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default FactOMeter;
